import { DesktopIcon, MoonIcon, SunIcon, type Icon as PhosphorIcon } from '@phosphor-icons/react';

import { track, useFeatureFlags } from '@/lib/telemetry';
import { useTheme } from '@/lib/theme/ThemeProvider';
import { THEME_TOGGLE_FLAG, type ThemeMode } from '@/lib/theme/theme-mode';
import { cn } from '@/lib/utils';

interface ThemeOptionSpec {
  mode: ThemeMode;
  label: string;
  Icon: PhosphorIcon;
}

const THEME_OPTIONS: ThemeOptionSpec[] = [
  { mode: 'light', label: 'Tema claro', Icon: SunIcon },
  { mode: 'dark', label: 'Tema escuro', Icon: MoonIcon },
  { mode: 'system', label: 'Tema do sistema', Icon: DesktopIcon },
];

/**
 * Light / dark / system switch for the top bar. Self-guards on the `theme-toggle`
 * feature flag (returns null when off), so dark stays opt-in until it's primed.
 */
export function ThemeToggle() {
  const enabled = useFeatureFlags(THEME_TOGGLE_FLAG);
  const { mode, setMode } = useTheme();

  if (!enabled) return null;

  return (
    <div
      role="radiogroup"
      aria-label="Tema"
      className="flex items-center gap-0.5 rounded-[10px] border border-border bg-surface p-0.5 shadow-[var(--shadow-1)]"
    >
      {THEME_OPTIONS.map((option) => (
        <ThemeOptionButton
          key={option.mode}
          option={option}
          active={mode === option.mode}
          onSelect={(next) => {
            if (next !== mode) track('action.settings.change_theme', { mode: next });
            setMode(next);
          }}
        />
      ))}
    </div>
  );
}

interface ThemeOptionButtonProps {
  option: ThemeOptionSpec;
  active: boolean;
  onSelect: (mode: ThemeMode) => void;
}

function ThemeOptionButton({ option, active, onSelect }: ThemeOptionButtonProps) {
  const selectThisMode = () => onSelect(option.mode);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={option.label}
      title={option.label}
      onClick={selectThisMode}
      className={cn(
        'flex size-7 cursor-pointer items-center justify-center rounded-lg transition-colors',
        active
          ? 'bg-surface-sunken text-text-primary'
          : 'text-text-tertiary hover:text-text-secondary',
      )}
    >
      <option.Icon size={16} weight={active ? 'fill' : 'regular'} />
    </button>
  );
}
